// @flow
/* global window */

import * as React from 'react';

import { useDispatch } from 'react-redux';

import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  fetchInfos,
  selectIsFetchingInfos,
  selectIsLoadedInfos,
} from '@gandi/billing';
import {
  mergeCart,
  selectCanChooseResellee,
  selectCartHasOrga,
  selectCartOwnerIsReseller,
  selectCartProductOwner,
  selectCartReselleeID,
  selectHasOwnerOnly,
  selectIsFailureCartOwnerList,
  setCartOwner,
  setCartOwnerIsReseller,
  setCartReselleeId,
  setCartUUID,
  setPricesCartWidget,
  selectCartUUID,
} from '@gandi/cart';
import { selectPersonalId } from '@gandi/redux-sharing';

import { useCartSummaryContext } from 'src/Layout/context/CartSummary';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { useHasMissingOrgInfo } from 'src/hooks/useMissingOrgInfo';

import { selectShopCurrent } from 'src/modules/organization/selectors';
import type {
  FullState,
  PreFetchArgs,
  PreFetchReturn,
  WithRouterProps,
} from 'src/types';

import { selectHasDomainTransfer } from '../Setup/modules/selectors';

import ActionsBlock from './components/ActionsBlock';
import BundleTypeWarning from './components/BundleTypeWarning';
import CorporateAlert from './components/CorporateAlert';
import IncompatibleBundleGrid from './components/IncompatibleBundleGrid';
import InvoiceBlock from './components/InvoiceBlock';
import OwnersBlock from './components/OwnersBlock';
import OwnershipDisclaimer from './components/OwnershipDisclaimer';
import {
  CUSTOMER,
  RESELLER,
  ORGANIZATION,
  type OwnerType,
} from './components/ResellerOwnerType';
import TaxAlert from './components/TaxAlert';
import { mustCartOwnerBeReseller } from './modules/helpers';
import {
  selectCartOwners,
  selectShouldRedirectToResellerOwnerId,
  selectResellerOwnerId,
  selectShouldRedirectToAccountForm,
  selectRedirectToNonMinimal,
  selectCanChooseProductOwner,
  selectCurrentOrganizationCanPay,
  selectCanChangeProductContacts,
} from './modules/selectors';

type Params = {
  cartId: string,
};

type Query = {
  guess?: string,
};

export type InjectedProps = {
  ...WithRouterProps<Params>,
};

export type Props = {
  ...InjectedProps,
};

export function selectFirstPurchaseUrl(state: FullState): string {
  const cartId = selectCartUUID(state);
  return `/cart/${cartId}/configure/account/create`;
}

function getDefaultOwnerType(
  chooseResellee: boolean,
  sharingId: string,
  resellee: ?string,
  owner: ?string,
  isOwnerReseller?: boolean,
  hasDomainTransfer?: boolean,
) {
  if (!chooseResellee) {
    return ORGANIZATION;
  }

  if (!resellee && owner && owner !== sharingId) {
    return ORGANIZATION;
  }

  if (isOwnerReseller && !hasDomainTransfer) {
    return RESELLER;
  }

  return CUSTOMER;
}

function Presenter(): React.MixedElement {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    frozen,
    handleConfigurationClick,
    setFrozen,
    ownerType,
    setOwnerType,
    updateSharingIdContext,
  } = useCartSummaryContext();

  const dispatch = useDispatch();

  const currentOrg = useAppSelector(state => selectShopCurrent(state, true));
  const sharingId = currentOrg?.id;

  const billingProps = { sharingId };

  const fetchingBillingInfo = useAppSelector(state =>
    selectIsFetchingInfos(state, billingProps),
  );
  const loadedBillingInfo = useAppSelector(state =>
    selectIsLoadedInfos(state, billingProps),
  );

  const personalId = useAppSelector(selectPersonalId);
  const missingCartOwnerInfo = useHasMissingOrgInfo(sharingId);

  const chooseResellee = useAppSelector(
    state => !!currentOrg?.reseller && selectCanChooseResellee(state),
  );
  const chooseOwner = useAppSelector(selectCanChooseProductOwner);

  const hasDomainTransfer = useAppSelector(selectHasDomainTransfer);

  const productOwner = useAppSelector(selectCartProductOwner);

  const missingProductOwnerInfo = useHasMissingOrgInfo(productOwner);
  const failureCartOwners = useAppSelector(state =>
    selectIsFailureCartOwnerList(state),
  );
  const cartOwners = useAppSelector(selectCartOwners);
  const canPay = useAppSelector(selectCurrentOrganizationCanPay);
  const resellee = useAppSelector(
    state => selectCartReselleeID(state) || undefined,
  );

  const hasOwnerOnly = useAppSelector(selectHasOwnerOnly);

  const redirectToAccountForm = useAppSelector(
    selectShouldRedirectToAccountForm,
  );

  // $FlowFixMe PartialOrganization should be compatible with CartOwnerCandidate
  const ownerMustReseller = mustCartOwnerBeReseller(hasOwnerOnly, cartOwners);
  const redirectToReseller = useAppSelector(
    selectShouldRedirectToResellerOwnerId,
  );
  const resellerOwner = useAppSelector(selectResellerOwnerId);
  const isOwnerReseller = useAppSelector(selectCartOwnerIsReseller);

  const redirectToNonMinimal = useAppSelector(selectRedirectToNonMinimal);
  const cartHasOrga = useAppSelector(selectCartHasOrga);

  const firstPurchaseUrl = useAppSelector(selectFirstPurchaseUrl);

  const canChangeProductContacts = useAppSelector(
    selectCanChangeProductContacts,
  );
  const handleChangeOwnerType: (currentOwnerType: OwnerType | null) => void =
    React.useCallback(
      currentOwnerType => {
        if (ownerType !== currentOwnerType) {
          setOwnerType(currentOwnerType);

          if (currentOwnerType === RESELLER) {
            dispatch(setCartOwnerIsReseller(true));
          }
        }
      },
      [ownerType, dispatch, setOwnerType],
    );

  const handleRedirectToAccountForm = React.useCallback(() => {
    navigate(firstPurchaseUrl, {
      replace: true,
    });
  }, [firstPurchaseUrl, navigate]);

  React.useEffect(() => {
    // Workaround as not implemented in JSDOM for tests
    if (process.env.NODE_ENV !== 'test') {
      window.scrollTo(0, 0);
    }
    setFrozen(false);
    // Alway fetch the current org's prepaid info
    // Necessary to:
    // - ensure current cart owner has a currency set
    // - check owner's grid against bundle grid limitations if any.
    const fetchAPI = async () => {
      if (!fetchingBillingInfo && !loadedBillingInfo) {
        await dispatch(fetchInfos());
      }
    };

    fetchAPI();

    if (redirectToAccountForm) {
      handleRedirectToAccountForm();
    }

    if (redirectToReseller) {
      updateSharingIdContext({ newSharingId: resellerOwner });
    }

    if (!searchParams.has('guess') && !cartHasOrga && redirectToNonMinimal) {
      updateSharingIdContext({
        newSharingId: redirectToNonMinimal,
        guess: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (redirectToAccountForm) {
      handleRedirectToAccountForm();
    }

    if (redirectToReseller) {
      updateSharingIdContext({ newSharingId: resellerOwner });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    handleRedirectToAccountForm,
    redirectToAccountForm,
    redirectToReseller,
    redirectToNonMinimal,
    frozen,
    searchParams,
    updateSharingIdContext,
  ]);

  React.useEffect(() => {
    if (
      !frozen &&
      !searchParams.has('guess') &&
      // If the user goes back after clicking on the "continue" button, then we won't switch the organization in the select
      !cartHasOrga &&
      redirectToNonMinimal
    ) {
      updateSharingIdContext({
        newSharingId: redirectToNonMinimal,
        guess: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectToNonMinimal]);

  React.useEffect(() => {
    dispatch(setPricesCartWidget(false));
    if (currentOrg) {
      handleChangeOwnerType(
        getDefaultOwnerType(
          chooseResellee,
          currentOrg.id,
          resellee,
          productOwner,
          isOwnerReseller,
          hasDomainTransfer,
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    chooseResellee,
    currentOrg?.id,
    resellee,
    productOwner,
    currentOrg?.reseller,
    hasDomainTransfer,
  ]);

  function handleOwnerChange(orgId: string | null): void {
    if (!orgId) return;

    if (currentOrg && currentOrg.reseller) {
      dispatch(setCartReselleeId(null));
    }

    updateSharingIdContext({
      newSharingId: orgId,
      guess: searchParams.has('guess') || orgId === personalId,
    });
  }

  const disabled =
    frozen ||
    !canPay ||
    !currentOrg ||
    (ownerMustReseller && !currentOrg.reseller) ||
    (!!(chooseResellee && !chooseOwner && currentOrg.reseller) && !resellee) ||
    !!(currentOrg.reseller && ownerType === CUSTOMER && !resellee);

  const missingInfo = missingCartOwnerInfo || missingProductOwnerInfo;

  return (
    <div>
      <InvoiceBlock
        ownerMustReseller={ownerMustReseller}
        failureCartOwners={failureCartOwners}
        onOwnerChange={handleOwnerChange}
        frozen={frozen}
      />
      {(chooseOwner || chooseResellee) && (
        <OwnersBlock
          key={currentOrg?.id}
          chooseOwner={chooseOwner}
          chooseResellee={chooseResellee}
          setOwnerType={handleChangeOwnerType}
        />
      )}
      <OwnershipDisclaimer />

      <CorporateAlert />
      <IncompatibleBundleGrid />
      <BundleTypeWarning />
      {productOwner && productOwner !== currentOrg?.id && <TaxAlert />}

      <ActionsBlock
        driveUserToSetup={missingInfo || hasDomainTransfer}
        canChangeSetup={canChangeProductContacts}
        disabled={disabled}
        onClickToSetup={handleConfigurationClick}
      />
    </div>
  );
}

Presenter.handleRedirect = async (
  args: PreFetchArgs<Props, Params, Query, FullState>,
) => {
  const { dispatch } = args;

  dispatch(setCartUUID(args.params.cartId));
  await InvoiceBlock.preFetch(args);
};

Presenter.preFetch = async (
  args: PreFetchArgs<Props, Params, Query, FullState>,
): PreFetchReturn<> => {
  const { dispatch, getState, params } = args;

  dispatch(setCartUUID(params.cartId));
  // Set the Cart Owner in a lite, fire-and-forget fashion.
  dispatch(setCartOwner({ headers: { 'X-Dry-Run': 0 } })).catch(() => {});
  // FIXME: `setCartOwner` is inconvenient to use in "fire-and-forget fashion"
  // because, even in lite mode it updates cart.isWorking flag which is used
  // by other components such as the CartWidget.
  dispatch(mergeCart({ isWorking: false }));

  const org = selectShopCurrent(getState(), true);

  const chooseResellee = org?.reseller && selectCanChooseResellee(getState());
  const chooseOwner = selectCanChooseProductOwner(getState());

  return Promise.allSettled([
    // $FlowFixMe[incompatible-call]
    CorporateAlert.preFetch(args),
    // $FlowFixMe[incompatible-call]
    (chooseResellee || chooseOwner) && OwnersBlock.preFetch(args),
  ]);
};

export default Presenter;
